<template>
  <teleport to="body">
    <div v-if="visible" @click.self="$emit('close')" class="core-modal-backdrop">
      <div class="core-modal-surface bg-white p-6 w-[480px] max-w-full border border-neutral-300">
        <h2 class="mb-4 font-semibold text-xl text-neutral-900">
          {{ wt('projectSubmission', 'modal', 'title') }}
        </h2>
        <p class="mb-6 text-base text-neutral-550">
          {{ wt('projectSubmission', 'modal', 'body') }}
        </p>
        <div class="flex gap-2">
          <button
            class="core-action-button core-action-button--primary h-11 flex-1"
            :disabled="downloading"
            @click="download">
            <span v-if="downloading">...</span>
            <span v-else>{{ wt('projectSubmission', 'modal', 'downloadButton') }}</span>
          </button>
          <button class="core-action-button core-action-button--supporting h-11 flex-1" @click="$emit('close')">
            {{ wt('projectSubmission', 'modal', 'closeButton') }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import {
  PdfMakeSubmissionGenerator,
  type SubmissionFormSnapshot,
  type SubmissionMetadata,
} from '@/services/pdf/pdfmake-submission-generator'
import * as Sentry from '@sentry/vue'

const { wt } = useWidgetText('widget-texts-project-summary')
const { wt: wtGeneral } = useWidgetText('widget-texts-general')

const props = defineProps<{
  visible: boolean
  formData: SubmissionFormSnapshot
  metadata: SubmissionMetadata
}>()

defineEmits<{
  (e: 'close'): void
}>()

const downloading = ref(false)

async function download() {
  downloading.value = true
  try {
    const generator = new PdfMakeSubmissionGenerator(props.formData, props.metadata, wt, wtGeneral)
    await generator.generate()
  } catch (error) {
    if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
      console.error('[SubmissionSuccessModal]', error)
    }
    Sentry.captureException(error)
  } finally {
    downloading.value = false
  }
}
</script>

<style lang="postcss" scoped>
.core-modal-backdrop {
  @apply fixed inset-0 overflow-y-auto overflow-x-hidden;
  @apply z-70 flex items-center justify-center;
  @apply bg-neutral-100/15 backdrop-blur-[2px];
}

.core-modal-surface {
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 8px 16px -12px rgba(0, 0, 0, 0.2);
}
</style>
